import { useSnackbar } from 'notistack'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import useBoothStatusButton from '~/features/circle-participation/hooks/useBoothStatusButton'
import { type CircleApplyDetailResponse } from '~/features/circle-participation/types/circle-apply'
import { usePostStartPayment } from '~/features/payment/queries/usePayment'
import { PaymentDivideType } from '~/features/payment/types/payment'
import Tag from '~/shared/components/tags/Tag'
import { isPaymentModalOpenState } from '~/shared/states'
import theme from '~/shared/styles/theme'
import { fCurrency } from '~/shared/utils/format-number'

type Props = {
  data: CircleApplyDetailResponse
}

function CircleApplyPaymentInfo({ data }: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const { id, booth_status, amount, discount_amount, total_amount, pay_limit } =
    data.userBoothApplication
  const { payment } = useBoothStatusButton(booth_status)
  const [_isPaymentModalOpen, setIsPaymentModalOpen] = useRecoilState(
    isPaymentModalOpenState,
  )

  const { mutate: paymentStartMutate } = usePostStartPayment()

  const requestPayment = () => {
    paymentStartMutate(
      {
        paymentInfo: {
          goods_id: Number(id) || 0,
          divide: PaymentDivideType.BOOTH,
        },
      },
      {
        onSuccess: (res) => {
          if (!res.errorMsg) {
            setIsPaymentModalOpen({
              contents: {
                productName: '서클 부스',
                amount: res.data.payment_info.total_amount,
                orderId: res.data.payment_info.order_code,
                userId: res.data.payment_info.user_id,
                paymentKey: res.data.payment_info.payment_key,
                paymentType: res.data.payment_info.payment_type,
              },
              isOpen: true,
            })
          } else {
            enqueueSnackbar(res.errorMsg, { variant: 'error' })
          }
        },
      },
    )
  }

  return (
    <Wrapper>
      <TopContainer>
        <SubTitleBox>
          <SubTitle>서클 참가비 결제 정보</SubTitle>
          {payment && <Button onClick={requestPayment}>결제하기</Button>}
        </SubTitleBox>
        <PaymentNotice>
          결제 완료 전, 신청 취소시에는 결제 정보가 보이지 않습니다.
        </PaymentNotice>
      </TopContainer>
      <PaymentInfoBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            참가비
          </Tag>
          <TagText>{fCurrency(amount) || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            할인금액
          </Tag>
          <TagText>{fCurrency(discount_amount) || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            합계
          </Tag>
          <TagText>{fCurrency(total_amount) || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            입금 기한
          </Tag>
          <TagText>{pay_limit || '-'}</TagText>
        </TagBox>
      </PaymentInfoBox>
    </Wrapper>
  )
}

export default CircleApplyPaymentInfo

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: flex-start;

  box-sizing: border-box;
  width: 100%;
  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  @media ${theme.device.medium} {
    padding: 24px 20px;
  }
`

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const SubTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const SubTitle = styled.h2`
  width: 100%;

  ${theme.fontStyle.subTitle};

  @media ${theme.device.medium} {
    ${theme.fontStyle.subTitleMobile};
    width: 100%;
    height: auto;
  }
`

const Button = styled.button`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  height: 58px;
  padding: 0 20px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: white;
  word-wrap: break-word;
  white-space: nowrap;

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 54px;

  @media ${theme.device.large} {
    width: auto;
  }

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const PaymentNotice = styled.p`
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 14px;
  color: ${theme.colors.grey_4};

  @media ${theme.device.medium} {
    line-height: 20px;
  }
`

const PaymentInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
`

const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const TagText = styled.span`
  ${theme.fontStyle.text};
  color: #313634;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    ${theme.fontStyle.textMobile};
  }
`
